// src/components/MainLayout.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, HelpCircle, Menu, LogOut, User } from 'lucide-react';
import Sidebar from './Navbar';
import NotificationBell from './NotificationBell';
import AuthTimeout from './AuthTimeout';

export default function MainLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeMenu, setActiveMenu] = useState('Tableau de bord');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [user, setUser] = useState({});

  // Charger l'utilisateur depuis localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (err) {
        console.error('Erreur parsing user:', err);
        setUser({ nom: 'Utilisateur', role: 'client' });
      }
    }
  }, []);

  const userNom = user.nom || 'Utilisateur';
  const userRole = user.role || 'client';
  const userInitial = userNom.charAt(0).toUpperCase();

  // Déterminer le titre de page basé sur l'URL
  useEffect(() => {
    const path = location.pathname;
    const menuTitles = {
      '/': 'Accueil',
      '/rapports': 'Tableau de bord',
      '/Statistiques': 'Statistiques',
      '/vehicules': 'Gestion des véhicules',
      '/vehicules/nouveau': 'Nouveau véhicule',
      '/demandes-reparation': 'Demandes de réparation',
      '/demandes-reparation/nouvelle': 'Nouvelle demande de réparation',
      '/reparations': 'Réparations',
      '/reparations/nouvelle': 'Nouvelle réparation',
      '/pieces': 'Pièces détachées',
      '/pieces/nouvelle': 'Nouvelle pièce',
      '/demandes/nouvelle': 'Nouvelle demande matériel',
      '/mes-demandes': 'Mes demandes',
      '/historiques': 'Historique',
      '/validateur': 'Validation des demandes',
      '/magasin': 'Gestion du magasin',
      '/magasin/recuperer': 'Récupération des demandes',
      '/Demandes-recuperer': 'Demandes à récupérer',
      '/validees-preparer': 'Demandes validées à préparer',
      '/notifications': 'Notifications',
      '/profile': 'Mon profil',
      '/change-password': 'Changer le mot de passe',
      '/conges/nouveau': 'Nouveau congé',
      '/mes-conges': 'Mes congés',
      '/admin/evenements': 'Gestion des Événements',
      '/admin/beneficiaires': 'Bénéficiaires',
      '/admin/packs-enterrement': 'Packs enterrement',
      '/admin/evenements/historique': 'Historique des événements',
      '/client/mes-evenements': 'Mes événements',
      '/client/evenements/nouveau': 'Nouvel événement',
      '/validateur/validation-evenements': 'Validation événements',
      '/magasinier/livraisons': 'Livraisons',
      '/admin/users': 'Gestion des utilisateurs',
      '/admin/categories': 'Gestion des Catégories',
      '/admin/materiels': 'Gestion des Matériels',
      '/admin/demandes': 'Toutes les demandes',
      '/admin/conges': 'Gestion des congés',
      '/validateur/conges': 'Validation des congés',
      '/chef/conges': 'Congés à confirmer',
      '/admin/my-demandes': 'Mes demandes (Admin)',
      '/admin/notifications': 'Notifications (Admin)',
    };

    if (menuTitles[path]) {
      setActiveMenu(menuTitles[path]);
    } else {
      const dynamicPaths = [
        { pattern: '/vehicules/', title: 'Détails du véhicule' },
        { pattern: '/demandes-reparation/', title: 'Détails de la demande' },
        { pattern: '/reparations/', title: 'Détails de la réparation' },
        { pattern: '/conge/', title: 'Détails du congé' },
        { pattern: '/demande-timeline/', title: 'Historique de la demande' },
        { pattern: '/demandes/historique/', title: 'Historique détaillé' },
        { pattern: '/validateur/demandes/', title: 'Validation de demande' },
        { pattern: '/magasin/preparer/', title: 'Préparation de demande' },
        { pattern: '/magasin/suivi/', title: 'Suivi de récupération' },
        { pattern: '/validateur/conge/', title: 'Traitement de congé' },
        { pattern: '/chef/conge/', title: 'Confirmation de congé' },
        { pattern: '/magasinier/livraison-materiels/', title: 'Gestion des matériels' },
        { pattern: '/magasinier/retours/', title: 'Gestion des retours' },
      ];

      const foundPath = dynamicPaths.find(dp => path.startsWith(dp.pattern));
      if (foundPath) {
        setActiveMenu(foundPath.title);
      } else {
        setActiveMenu('Tableau de bord');
      }
    }
  }, [location.pathname]);

  // Fermer le menu utilisateur en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserMenu]);

  const getRoleColor = (role) => {
    const colors = {
      'admin': 'bg-red-100 text-red-700',
      'client': 'bg-blue-100 text-blue-700',
      'validateur': 'bg-green-100 text-green-700',
      'chef_equipe': 'bg-purple-100 text-purple-700',
      'magasinier': 'bg-orange-100 text-orange-700',
      'mecanicien': 'bg-cyan-100 text-cyan-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  const handleMenuClick = (menuTitle) => {
    setActiveMenu(menuTitle);
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login', { replace: true });
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar pour desktop */}
      <div className="hidden lg:block">
        <Sidebar 
          onMenuClick={handleMenuClick} 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
        />
      </div>

      {/* Menu mobile */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl">
            <Sidebar 
              onMenuClick={handleMenuClick} 
              isCollapsed={false}
            />
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className={`flex-1 transition-all duration-300 ${
        isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
      }`}>
        {/* Header fixe */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
              
              <div>
                <h1 className="text-base lg:text-lg font-semibold text-gray-900">
                  {activeMenu}
                </h1>
                <p className="text-xs text-gray-500 mt-0.5 hidden lg:block">
                  {location.pathname === '/' 
                    ? 'Bienvenue sur votre tableau de bord' 
                    : `Centre Régional Mansouria`}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <NotificationBell />
              
              <button 
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Aide"
              >
                <HelpCircle className="w-5 h-5 text-gray-600" />
              </button>
              
              {/* Menu utilisateur */}
              <div className="flex items-center space-x-3 pl-3 border-l border-gray-200 user-menu-container relative">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{userNom}</p>
                  <p className="text-xs text-gray-500 capitalize">{userRole.replace('_', ' ')}</p>
                </div>
                
                <button
                  onClick={toggleUserMenu}
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${getRoleColor(userRole)} hover:opacity-90 transition-opacity`}
                >
                  {userInitial}
                </button>

                {/* Dropdown menu utilisateur */}
                {showUserMenu && (
                  <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{userNom}</p>
                        <p className="text-xs text-gray-500">{userRole.replace('_', ' ')}</p>
                      </div>
                      
                      <a
                        href="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <User className="w-4 h-4 mr-2" />
                        Mon profil
                      </a>
                      
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowLogoutConfirm(true);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-6">
          <div className="bg-white rounded-lg border min-h-[calc(100vh-140px)]">
            {children}
          </div>
        </main>
      </div>

      {/* Timer d'authentification */}
      <AuthTimeout />

      {/* Modal de confirmation de déconnexion */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowLogoutConfirm(false)}
          />
          
          <div className="relative bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <LogOut className="w-6 h-6 text-red-600" />
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Déconnexion
            </h3>
            
            <p className="text-gray-600 text-center text-sm mb-6">
              Êtes-vous sûr de vouloir vous déconnecter ?
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}